import { AsyncThunk, ThunkDispatch } from '@reduxjs/toolkit';
import {
  FilterChangedEvent,
  RowDataUpdatedEvent,
  RowSelectedEvent,
} from 'ag-grid-community';
import { ColumnApi, GridApi } from 'ag-grid-enterprise';
import {
  addSelectedOrchestration,
  removeSelectedOrchestration,
  resetSort,
  SelectActiveFilterType,
  SelectFilterInfoType,
  SelectFilterModelType,
  SelectPaginationInfoType,
  setFilterInfo,
  setFilterModel,
  setPaginatorInfo,
} from 'redux/dashboard/DashboardSlice';
import { TABLE_FIELDS } from 'utils/common-constants';
import { mapJobToTableData } from 'dto/jobToTable/jobToTable';
import { IOrchestrationRow } from 'interfaces/dashboard/orchestrationRow.interface';
import {
  IPostJobsInfo,
  PostJobsInfoSortigFields,
} from 'interfaces/postJobsInfo.interface';
import {
  IOnGridReadyAdditionalMethod,
  PAGE_OPTIONS,
} from 'interfaces/table.interface';
import { MutableRefObject } from 'react';
import {
  deleteTableFiltersFromDataToSend,
  setFilterDataToSend,
} from './filterUtils';
import {
  consoleErrorMessage,
  isObjectsAreEqualDeep,
} from 'utils/commonFunctions/CommonFunctions';
import { IJobStartedByUser } from 'interfaces/user.interface';
import { IOrchestrationServer } from 'interfaces/orchestration.interface';

export enum FILTER_NAMES {
  ORCHESTRATION_NAME = 'ORCHESTRATION_NAME',
  START_TIME = 'START_TIME',
  END_TIME = 'END_TIME',
  USER = 'USER',
  DURATION = 'DURATION',
  ORIGINAL_FILE_NAME = 'ORIGINAL_FILE_NAME',
  DESCRIPTION = 'DESCRIPTION',
  WORKER = 'WORKER',
  THROTTLE_RATE = 'THROTTLE_RATE',
  OPTIONS = 'OPTIONS',
  CREATED_TIME = 'CREATED_TIME',
  MESSAGE = 'MESSAGE',
  SEVERITY = 'SEVERITY',
  VERSION = 'VERSION',
  WORKERS = 'WORKERS',
  CONSUMERS = 'CONSUMERS',
  TOTAL_CAPACITY = 'TOTAL_CAPACITY',
  TYPE = 'TYPE',
  REQUIRED = 'REQUIRED',
  DEFAULT = 'DEFAULT',
}

export const filterIds: Partial<Record<TABLE_FIELDS, FILTER_NAMES>> = {
  [TABLE_FIELDS.name]: FILTER_NAMES.ORCHESTRATION_NAME,
  [TABLE_FIELDS.startTime]: FILTER_NAMES.START_TIME,
  [TABLE_FIELDS.endTime]: FILTER_NAMES.END_TIME,
  [TABLE_FIELDS.user]: FILTER_NAMES.USER,
  [TABLE_FIELDS.duration]: FILTER_NAMES.DURATION,
  [TABLE_FIELDS.originalFileName]: FILTER_NAMES.ORIGINAL_FILE_NAME,
  //run orchestration
  [TABLE_FIELDS.description]: FILTER_NAMES.DESCRIPTION,
  [TABLE_FIELDS.worker]: FILTER_NAMES.WORKER,
  [TABLE_FIELDS.throttleRate]: FILTER_NAMES.THROTTLE_RATE,
  [TABLE_FIELDS.options]: FILTER_NAMES.OPTIONS,
  // error-log modal fields
  [TABLE_FIELDS.createdTime]: FILTER_NAMES.CREATED_TIME,
  [TABLE_FIELDS.message]: FILTER_NAMES.MESSAGE,
  [TABLE_FIELDS.severity]: FILTER_NAMES.SEVERITY,
  //installed modules fields
  [TABLE_FIELDS.version]: FILTER_NAMES.VERSION,
  [TABLE_FIELDS.workers]: FILTER_NAMES.WORKERS,
  [TABLE_FIELDS.consumers]: FILTER_NAMES.CONSUMERS,
  [TABLE_FIELDS.totalCapacity]: FILTER_NAMES.TOTAL_CAPACITY,
  //module details fields
  [TABLE_FIELDS.type]: FILTER_NAMES.TYPE,
  [TABLE_FIELDS.required]: FILTER_NAMES.REQUIRED,
  [TABLE_FIELDS.default]: FILTER_NAMES.DEFAULT,
};

//Sorting
export const handleSort = (
  sorted: any,
  filterInfo: IPostJobsInfo | undefined,
  dispatch: ThunkDispatch<any, any, any>,
  gridColumnApiRef: MutableRefObject<ColumnApi | null>,
  gridApiRef: MutableRefObject<GridApi<any> | null>,
  page: PAGE_OPTIONS | undefined
) => {
  if (!sorted) {
    dispatch(resetSort());
  }

  const sortedId: TABLE_FIELDS = sorted?.colId;
  const sortOrder: 'asc' | 'desc' = sorted?.sort === 'asc' ? 'desc' : 'asc';
  if (
    sorted &&
    sortedId !== TABLE_FIELDS.status &&
    sortedId !== TABLE_FIELDS.step &&
    sortedId !== TABLE_FIELDS.id &&
    page === PAGE_OPTIONS.orchestrations
  ) {
    dispatch(
      setFilterInfo({
        ...filterInfo,
        activePage: 1,
        sortingField: filterIds[sortedId] as PostJobsInfoSortigFields,
        sortingType: sortOrder.toUpperCase() as 'ASC' | 'DESC',
      })
    );
  }
  const columnState = gridColumnApiRef.current?.getColumns();
  if (columnState?.length) {
    columnState.forEach((item) => {
      if (item.getSort() != null) {
        gridColumnApiRef.current?.autoSizeColumn(item.getColId() || '');
        gridApiRef.current?.sizeColumnsToFit();
      } else if (item.getColId() === sortedId && item.getSort() === undefined) {
        const reverseSortOrder: 'asc' | 'desc' =
          sortOrder === 'desc' ? 'asc' : 'desc';
        gridColumnApiRef.current?.applyColumnState({
          state: [
            { colId: item.getColId(), sort: reverseSortOrder, sortIndex: 0 },
          ],
        });
      }
    });
  }
};

//Filtering
export const handleFilter = (
  event: FilterChangedEvent,
  filterModel: MutableRefObject<SelectFilterModelType>,
  isServerSideFiltering: boolean | undefined = false,
  filterInfo: SelectFilterInfoType,
  usersRef: MutableRefObject<IJobStartedByUser[]>,
  dispatch: ThunkDispatch<any, any, any>,
  paginatorInfo: SelectPaginationInfoType
) => {
  const filterModelFromApi = event.api.getFilterModel();
  if (isObjectsAreEqualDeep(filterModel?.current, filterModelFromApi)) {
    return;
  }
  const dataToSend = {
    ...filterInfo,
  };
  if (isServerSideFiltering) {
    deleteTableFiltersFromDataToSend(dataToSend);
    setFilterDataToSend(
      filterModelFromApi,
      event.api,
      dataToSend,
      usersRef.current
    );
  }
  dispatch(setFilterModel(filterModelFromApi));
  dispatch(setPaginatorInfo({ ...paginatorInfo, page: 1 }));
  if (Object.keys(filterModelFromApi).length === 0) {
    dispatch(setFilterInfo({}));
  } else {
    dispatch(setFilterInfo(dataToSend));
  }
};

export const handleRowSelection = (
  event: RowSelectedEvent,
  dispatch: ThunkDispatch<any, any, any>
) => {
  try {
    const isSelected = event.node.isSelected();
    const data = { ...event.node.data } as IOrchestrationRow;

    // Dispatch selection change based solely on current node state
    if (isSelected) {
      dispatch(addSelectedOrchestration(data));
    } else {
      dispatch(removeSelectedOrchestration(data));
    }
  } catch (error: any) {
    consoleErrorMessage(error);
  }
};

export const handleRowDataUpdated = (
  event: RowDataUpdatedEvent,
  gridColumnApiRef: MutableRefObject<ColumnApi | null>,
  gridApiRef: MutableRefObject<GridApi<any> | null>,
  activeFilters: MutableRefObject<SelectActiveFilterType> | null,
  onGridReadyAdditionalMethods: IOnGridReadyAdditionalMethod[] | undefined
) => {
  // Removed selection toggling based on data.isSelected to prevent overwriting programmatic selection

  onGridReadyAdditionalMethods?.map(
    (additionalMethod: IOnGridReadyAdditionalMethod) => {
      if (additionalMethod.id === 'openSeverityRows') {
        additionalMethod.method(gridApiRef);
      }
      if (additionalMethod.id === 'updateDurationColumn') {
        const orchestrations: IOrchestrationRow[] = [];
        event.api.forEachNode((rowNode: any) => {
          orchestrations.push(rowNode.data);
        });
        additionalMethod.method(gridApiRef, orchestrations);
      }
    }
  );

  activeFilters?.current?.forEach((filter) => {
    gridColumnApiRef.current?.getColumn(filter.id)?.setFilterActive(true);
    gridColumnApiRef.current?.autoSizeColumn(filter.id);
  });
};
